import { useContext } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LoginFormSchema, loginSchema } from './formSchema';
import { AuthenticationBaseLayout } from '@/components/ui/authentication/authenticationBaseLayout';
import { useToast } from '@/hooks/useToast';
import { AuthContext } from '@/providers/authProvider';

export const Login = () => {
  const form = useForm<LoginFormSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { loginMutation } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  // Where to go after login: the page the user was sent here from (e.g. a
  // /cart/:id/toggle link), or /home by default.
  const from = (location.state as { from?: string } | null)?.from || '/home';

  const onSubmit = (data: { email: string; password: string }) => {
    loginMutation.mutate(data, {
      onSuccess: () => {
        navigate(from);
        form.reset();
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: 'Invalid Email or Password',
        });
        console.error(error);
      },
    });
  };

  return (
    <AuthenticationBaseLayout>
      <div className="flex flex-col items-start w-full max-w-sm p-6 lg:w-auto lg:max-w-none">
        <div className="flex flex-col justify-start items-start mb-4 text-left">
          <h1 className="text-base font-bold text-card-foreground">Sign In</h1>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
            className="space-y-4 w-full lg:w-auto"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="text-left">
                  <FormLabel className="text-card-foreground text-sm">
                    Email *
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full lg:w-[352px]"
                      placeholder="example@email.com"
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="text-left">
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-card-foreground text-sm">
                      Password *
                    </FormLabel>
                    <a
                      href="/forgot-password"
                      className="text-sm font-normal text-muted-foreground underline"
                    >
                      Forgot your password?
                    </a>
                  </div>
                  <FormControl>
                    <Input
                      className="w-full lg:w-[352px]"
                      {...field}
                      placeholder="*********"
                      type="password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={loginMutation.isPending}>
                Sign In
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AuthenticationBaseLayout>
  );
};
